import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { certificationServices, coins } from '../../utils/api';
import PurchaseTransactionModal from '../PurchaseTransactionModal';
import { BatchFormTable } from './components/BatchFormTable';
import { useFormFields } from './hooks/useFormFields';
import { useImportLogs } from './hooks/useImportLogs';
import { useRowManager } from './hooks/useRowManager';
import { useCellSelection } from './hooks/useCellSelection';
import { usePasteHandler } from './hooks/usePasteHandler';
import { useImportManager } from './hooks/useImportManager';
import { useCertLookup } from './hooks/useCertLookup';

const BatchCoinForm = () => {
  const navigate = useNavigate();
  const [servicesData, setServicesData] = React.useState([]);
  const [statusesData, setStatusesData] = React.useState([]);
  
  // Memoize services and statuses to prevent unnecessary re-renders
  const services = useMemo(() => servicesData, [servicesData]);
  const statuses = useMemo(() => statusesData, [statusesData]);

  // Initialize hooks
  const { fields, error: fieldsError } = useFormFields(services, statuses);
  const { logs, addLog, LogViewer } = useImportLogs();
  const {
    rows,
    setRows,
    handleAddRow,
    handleRemoveRow,
    handleCellChange,
    handleKeyDown,
    cleanRowData
  } = useRowManager();

  const {
    selectedCells,
    isDragging,
    dragField,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp,
    clearSelection
  } = useCellSelection();

  const { handlePaste } = usePasteHandler({
    rows,
    setRows,
    selectedCells,
    services,
    fields
  });

  const {
    loading,
    error: importError,
    showTransactionModal,
    createdCoins,
    failedImports,
    handleImport,
    handleTransactionSubmit,
    setShowTransactionModal,
    importProgress,
  } = useImportManager({ addLog, navigate });

  // Fetch certification services and statuses
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const [servicesData, statusesData] = await Promise.all([
          certificationServices.getAll(),
          coins.getPossibleStatuses()
        ]);
        if (isMounted) {
          setServicesData(servicesData);
          setStatusesData(statusesData);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        if (isMounted) {
          addLog('Failed to load required data', 'error');
        }
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []); // Remove addLog from dependencies since it's only used for error handling

  const { handleFetchCertDetails, fetchProgress } = useCertLookup({
    rows,
    setRows,
    services,
    addLog
  });

  return (
    <div className="space-y-6">
      {(fieldsError || importError || failedImports.length > 0) && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              {(fieldsError || importError) && (
                <pre className="text-sm font-medium text-red-800 whitespace-pre-wrap mb-2">
                  {fieldsError || importError}
                </pre>
              )}
              {failedImports.length > 0 && (
                <div className="text-sm text-red-800">
                  <p className="font-medium mb-1">Failed Imports:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {failedImports.map((fail, index) => (
                      <li key={index}>
                        Row {fail.rowNumber}: {fail.description} - {fail.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow sm:rounded-lg w-full">
        <div className="px-4 py-5 sm:p-6 overflow-visible">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Batch Import</h3>
              <div className="space-x-3">
              <button
                type="button"
                onClick={handleAddRow}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Add Row
              </button>
              <button
                type="button"
                onClick={() => !loading && fetchProgress.total === 0 && handleFetchCertDetails()}
                disabled={loading || fetchProgress.total > 0}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
              >
                {fetchProgress.total > 0 
                  ? `Fetching... (${fetchProgress.current}/${fetchProgress.total})`
                  : loading 
                    ? 'Fetching...' 
                    : 'Fetch Cert Details'}
              </button>
              <button
                type="button"
                onClick={() => handleImport(rows, cleanRowData)}
                disabled={loading || rows.length === 0}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
              >
                {importProgress.total > 0 
                  ? `Importing... (${importProgress.current}/${importProgress.total})`
                  : loading 
                    ? 'Importing...' 
                    : 'Import All'}
              </button>
              </div>
            </div>
          </div>

          <div className="mt-2 max-w-xl text-sm text-gray-500 mb-4">
            <p>Enter coin details below or paste data from a spreadsheet. You can paste multiple rows at once. For certified coins, select a service and enter the certification number, then click "Fetch Cert Details" to auto-fill available information.</p>
          </div>

          <BatchFormTable
            fields={fields}
            rows={rows}
            services={services}
            selectedCells={selectedCells}
            isDragging={isDragging}
            onCellMouseDown={handleCellMouseDown}
            onCellMouseEnter={handleCellMouseEnter}
            onCellMouseUp={handleCellMouseUp}
            onPaste={handlePaste}
            onCellChange={handleCellChange}
            onKeyDown={handleKeyDown}
            onRemoveRow={(index) => {
              handleRemoveRow(index);
              clearSelection();
            }}
          />
        </div>
      </div>

      <LogViewer />

      {/* Purchase Transaction Modal */}
      {showTransactionModal && (
        <PurchaseTransactionModal
          isOpen={showTransactionModal}
          onClose={() => {
            setShowTransactionModal(false);
            navigate('/inventory');
          }}
          onSubmit={handleTransactionSubmit}
          coins={createdCoins.map(coin => ({
            id: coin.id,
            user_coin_id: coin.user_coin_id,
            denomination: coin.denomination,
            year: coin.year,
            mint_mark: coin.mint_mark,
            variety: coin.variety,
            die_variety: coin.die_variety,
            minor_variety: coin.minor_variety,
            grade: coin.grade,
            certification_number: coin.certification_number,
            certification_service_id: coin.certification_service_id,
            purchase_price: coin.purchase_price || 0,
            list_price: coin.list_price || 0,
            date: coin.purchase_date,
            status: coin.status,
            transactionType: 'purchase'
          }))}
        />
      )}
    </div>
  );
};

export default BatchCoinForm;
